import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import {
  api,
  type ProjectFolderDetail,
  type StructureType,
  type ShareProjectRequest,
  type ShareProjectResponse,
} from '../lib/api';
import { useAuth } from '../lib/auth';

/** Post-create order confirmation for the main-menu project-folder flow. */
export function ProjectSummaryPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareOpen, setShareOpen] = useState(false);
  const [quoteMode, setQuoteMode] = useState(false);
  // `id` may be a numeric id (in-app links) or a hashed share token (email
  // links); the API decodes both transparently, so pass it through untouched.
  const projectQuery = useQuery({
    queryKey: ['project-folder', id],
    queryFn: () => api.get<ProjectFolderDetail>(`/api/v1/project-folders/${id}`),
    enabled: !!id,
  });

  // Deep-link from the project "Request your quote" email button
  // (`/admin/projects/{token}/summary?directshare=1`): open the Share modal in
  // quote mode automatically, then strip the flag so a refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('directshare') !== '1') return;
    setQuoteMode(true);
    setShareOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('directshare');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const typesQuery = useQuery({
    queryKey: ['structure-types'],
    queryFn: () => api.get<StructureType[]>('/api/v1/structure-types'),
  });

  if (projectQuery.isLoading) {
    return <SummaryShell><p className="text-muted text-center">Loading project summary…</p></SummaryShell>;
  }

  if (projectQuery.error || !projectQuery.data) {
    return (
      <SummaryShell>
        <div className="alert alert-danger">Project summary could not be loaded.</div>
        <Link to="/" className="btn btn-outline-secondary">Return to Menu</Link>
      </SummaryShell>
    );
  }

  const project = projectQuery.data;
  const hasStructures = project.structures.length > 0;
  const typesById = new Map((typesQuery.data ?? []).map((t) => [t.id, t]));
  return (
    <SummaryShell>
      <div className="text-center mb-4">
        <h1 className="mb-2">Project Folder: {project.name}</h1>
        <p className="lead mb-0">Review and confirm accuracy of structure details below before distributing.</p>
      </div>

      {hasStructures ? (
        <>
          {project.structures.map((structure) => (
            <div className="row mt-3" key={structure.id}>
              <div className="col-12 col-lg-2 d-none d-lg-block">
                <div className="m-2 text-center">
                  <img
                    className="img-fluid"
                    style={{ maxWidth: 130 }}
                    src={productImage(structure.structureTypeId != null ? typesById.get(structure.structureTypeId) : undefined)}
                    alt=""
                    onError={(ev) => { (ev.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-10">
                <div className="m-2 p-2 border">
                  <div className="row justify-content-center">
                    <div className="col-12 col-lg pe-lg-5">
                      <div className="fw-bold">
                        <Link className="link-dark" to={`/structurereport/view/${structure.id}`}>
                          {structure.nickname || `Structure #${structure.id}`}
                        </Link>
                      </div>
                      <div>Style Number: {structure.styleNumber || '—'}</div>
                      <div>
                        Filter Type: {structure.permanent ? 'Permanent' : 'Temporary'} | Oil Removal:{' '}
                        {structure.oilFiltration ? 'Yes' : 'No'}
                      </div>
                      <Link to={`/structurereport/view/${structure.id}`} className="btn btn-sm btn-primary mt-2">
                        View Structure Details
                      </Link>
                    </div>
                    <div className="col-auto border-start p-0 d-none d-lg-block" />
                    <div className="col-12 col-lg px-lg-5 pt-2 pt-lg-0">
                      <div className="fw-bold">Order Info</div>
                      <div>Style Number: {structure.styleNumber || '—'}</div>
                      {structure.foundryPartNumber && (
                        <>
                          <div>Foundry: {structure.foundryName || '—'}</div>
                          <div>Casting Number: {structure.foundryPartNumber}</div>
                          {structure.foundryPartLineNote && <div>Line Note: {structure.foundryPartLineNote}</div>}
                        </>
                      )}
                      {structure.hangerKitTransitionPlatePartNumber && (
                        <div>Hanger Kit / Transition Plate: {structure.hangerKitTransitionPlatePartNumber}</div>
                      )}
                      {orderInfoDimensions(structure).map((row) => (
                        <div key={row.label}>
                          <span>{row.label} </span>
                          <span>{row.value}"</span>
                        </div>
                      ))}
                    </div>
                    <div className="col-auto border-start p-0 d-none d-lg-block" />
                    <div className="col-12 col-lg ps-lg-5 pt-2 pt-lg-0">
                      <div className="fw-bold">Quantity</div>
                      <div>{structure.quantity}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="m-0 mt-2 row justify-content-center">
            <div className="col-12 col-lg-6 p-2 border">
              <span className="p-0 fw-bold fs-6">Project Information</span>
              <InfoRow label="Project Name" value={project.name} />
              <InfoRow label="Design Engineer" value={project.designEngineer} />
              <InfoRow label="Contractor" value={project.siteContractor} />
              <InfoRow label="Location" value={[project.state, project.city].filter(Boolean).join(', ')} />
              <InfoRow label="Project Last Modified" value={project.updatedAt ? new Date(project.updatedAt).toLocaleString() : ''} />
              {project.additionalNotes && (
                <div className="py-1">
                  <span className="fw-bold">Additional Project Notes</span>
                  <br />
                  <span className="fw-bold text-success">{project.additionalNotes}</span>
                </div>
              )}
            </div>
          </div>

          <div className="row m-3">
            <div className="col-12 text-center">
              <button
                type="button"
                className="btn btn-success m-2"
                onClick={() => setShareOpen(true)}
              >
                Share Or Submit
              </button>
              <a
                href={`/project/pdf/${project.id}`}
                target="_blank"
                rel="noopener"
                className="btn btn-success m-2"
              >
                Download Project PDF
              </a>
              <Link to={`/project/addstructure/${project.id}`} className="btn btn-success m-2">
                Update/Edit Project
              </Link>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="row m-3">
            <div className="col-12 text-center fs-4">
              This project currently does not have any structures added. Add structures to this project to more
              easily share with ADS to get a quote.
            </div>
          </div>
          <div className="col-12 text-center">
            <Link to="/newstructure" className="btn btn-sm btn-outline-primary m-2">Create New Structure</Link>
            <Link to={`/project/addstructure/${project.id}`} className="btn btn-sm btn-outline-primary m-2">
              Add Project Structures
            </Link>
          </div>
        </>
      )}

      {shareOpen && (
        <ShareProjectModal projectId={project.id} quoteMode={quoteMode} onClose={() => setShareOpen(false)} />
      )}
    </SummaryShell>
  );
}

function ShareProjectModal({ projectId, quoteMode = false, onClose }: { projectId: number; quoteMode?: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sendToAccount, setSendToAccount] = useState(true);
  const [shareToOther, setShareToOther] = useState(false);
  const [sendToAds, setSendToAds] = useState(quoteMode);
  const [tosAgree, setTosAgree] = useState(false);
  const [optInNewsletter, setOptInNewsletter] = useState(true);
  const [recipient, setRecipient] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const share = useMutation({
    mutationFn: (body: ShareProjectRequest) =>
      api.post<ShareProjectResponse>(`/api/v1/project-folders/${projectId}/share`, body),
    onSuccess: (res) => {
      setMessage(res.message || 'Project shared.');
      qc.invalidateQueries({ queryKey: ['project-folder', projectId] });
      qc.invalidateQueries({ queryKey: ['project-deliveries', projectId] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Share failed'),
  });

  const submit = () => {
    setError(null);
    setMessage(null);
    if (!tosAgree) {
      setError('Please agree to the Terms & Conditions before submitting your project.');
      return;
    }
    const recipients = shareToOther && recipient.trim() ? [recipient.trim()] : [];
    if (!sendToAccount && !sendToAds && recipients.length === 0) {
      setError('Pick at least one destination (your account, ADS, or a recipient email).');
      return;
    }
    share.mutate({ sendToAccount, sendToAds, recipients, optInNewsletter });
  };

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="d-flex justify-content-end">
            <button type="button" className="btn-close p-3" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body mt-0 pt-0 m-3 px-4">
            <h1 className="text-center">Share Your Project</h1>
            <p className="text-center">Please choose from the options below to share or submit your structure</p>

            <div className="row justify-content-center">
              <div className="col-12 col-md-6 px-3 pt-1">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="send_to_account"
                    checked={sendToAccount}
                    onChange={(e) => setSendToAccount(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="send_to_account">
                    Send to my account email
                  </label>
                </div>
              </div>
              <div className="col-6 d-none d-md-block px-3 pt-1 text-end">
                <label className="text-black-50">{user?.email}</label>
              </div>

              <div className="col-12 px-3 pt-1">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="send_to_email"
                    checked={shareToOther}
                    onChange={(e) => setShareToOther(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="send_to_email">
                    Share to another email address
                  </label>
                </div>
                {shareToOther && (
                  <div className="ps-3 pt-2">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email Address"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="col-12 px-3 pt-1">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="send_to_ADS"
                    checked={sendToAds}
                    onChange={(e) => setSendToAds(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="send_to_ADS">
                    Send to ADS for quoting
                  </label>
                </div>
              </div>

              <div className="col-12 px-3 pt-3">
                <hr className="my-2" />
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="tos_agree"
                    checked={tosAgree}
                    onChange={(e) => setTosAgree(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="tos_agree">
                    I agree to the{' '}
                    <a
                      href="https://www.adspipe.com/terms-conditions/terms-and-conditions-sales"
                      target="_blank"
                      rel="noreferrer"
                      className="fw-semibold text-success"
                    >
                      Terms &amp; Conditions
                    </a>
                  </label>
                </div>
              </div>

              <div className="col-12 px-3 pt-3">
                <hr className="my-2" />
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="opt_in_newsletter"
                    checked={optInNewsletter}
                    onChange={(e) => setOptInNewsletter(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="opt_in_newsletter">
                    Sign me up to receive the latest news and product information.
                  </label>
                </div>
              </div>
            </div>

            {message && <div className="alert alert-success mt-3">{message}</div>}
            {error && <div className="alert alert-danger mt-3">{error}</div>}

            <div className="text-center mt-3">
              <button type="button" className="btn btn-success" disabled={share.isPending} onClick={submit}>
                {share.isPending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface OrderInfoRow {
  label: string;
  value: number | string;
}

// Mirrors StructureReportPage's casting dimension formatting: round castings
// report a single diameter, rectangular castings report "width x height".
function castingDimension(
  width: number | null,
  height: number | null,
  diameter: number | null,
): string | null {
  if (diameter != null && Number(diameter) > 0) return `${Number(diameter)}`;
  if (width != null && height != null && (Number(width) > 0 || Number(height) > 0)) {
    return `${Number(width)} x ${Number(height)}`;
  }
  return null;
}

function productImage(type: StructureType | undefined): string {
  if (!type) return '/images/PRODUCT/Product-PrecastConcrete.png';
  if (type.is4SeatSquare) return '/images/PRODUCT/Precast-4Seat.png';
  if (type.is2SeatLong) return '/images/PRODUCT/Precast-2SeatLong.png';
  if (type.is2SeatShort) return '/images/PRODUCT/Precast-2SeatShort.png';
  if (type.isNyloplast) return '/images/PRODUCT/Product-Nyloplast.png';
  if (type.isCastIronRound) return '/images/PRODUCT/CastIron-Round.png';
  if (type.isCastIronCurbBoxCombo) return '/images/PRODUCT/CastIron-CurbBox.png';
  if (type.isCastIronRectangular) return '/images/PRODUCT/CastIron-FlatRectangular.png';
  if (type.isOpenThroat) return '/images/PRODUCT/Product-OpenThroat.png';
  if (type.isCastIron) return '/images/PRODUCT/Product-CastIron.png';
  return '/images/PRODUCT/Product-PrecastConcrete.png';
}

function orderInfoDimensions(s: ProjectFolderDetail['structures'][number]): OrderInfoRow[] {
  // Casting-based structures carry their grate/opening dimensions on the
  // foundry part (legacy project_folder/view.blade.php), not the structure.
  if (s.foundryPartNumber) {
    const rows: OrderInfoRow[] = [];
    const grate = castingDimension(
      s.foundryPartGrateSizeWidth,
      s.foundryPartGrateSizeHeight,
      s.foundryPartGrateSizeDiameter,
    );
    if (grate) {
      rows.push({
        label:
          s.foundryPartGrateSizeDiameter != null && Number(s.foundryPartGrateSizeDiameter) > 0
            ? 'Diameter of Grate'
            : 'Grate Size',
        value: grate,
      });
    }
    const opening = castingDimension(
      s.foundryPartOpeningSizeWidth,
      s.foundryPartOpeningSizeHeight,
      s.foundryPartOpeningSizeDiameter,
    );
    if (opening) {
      rows.push({ label: 'Opening of Concrete', value: opening });
    }
    return rows;
  }

  const candidates: Array<[string, number | null]> = [
    ['Diameter of Grate', s.grateDiameter],
    ['Clear Opening Diameter', s.frameClearOpeningDiameter],
    ['Grate Size Left To Right', s.grateSizeLeftToRight],
    ['Grate Size Front To Back', s.grateSizeFrontToBack],
    ['Clear Opening Left To Right', s.clearOpeningLeftToRight],
    ['Clear Opening Front To Back', s.clearOpeningFrontToBack],
    ['Grate Size Long Span', s.grateSizeLongSpan],
    ['Grate Size Short Span', s.grateSizeShortSpan],
    ['Clear Opening Long Span', s.clearOpeningLongSpan],
    ['Clear Opening Short Span', s.clearOpeningShortSpan],
    ['Curb Opening Width', s.curbOpeningWidth],
    ['Catch Basin Wall To Wall Inside Dimension', s.catchBasinWallToWallInsideDimension],
  ];
  return candidates
    .filter(([, v]) => v != null && Number(v) > 0)
    .map(([label, v]) => ({ label, value: Number(v) }));
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="py-1 d-flex justify-content-between">
      <span className="fw-bold">{label}</span>
      <span className="fw-bold text-success">{value || '—'}</span>
    </div>
  );
}

function SummaryShell({ children }: { children: ReactNode }) {
  return <div className="container-fluid py-3 py-lg-4">{children}</div>;
}